/**
 * My Tickets Page
 * Displays the current user's tickets with filtering
 */

import { useState, useEffect } from 'react'
import { Box, Container, Typography, Button, Tabs, Tab, Alert } from '@mui/material'
import { Add, Refresh } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { getMyTickets } from '@api/services/tickets.api'
import { TicketSummaryDto, TicketStatus } from '@api/types/tickets'
import { TicketList } from '../components/TicketList'

type FilterTab = 'all' | 'open'

export default function MyTicketsPage() {
  const navigate = useNavigate()
  const [allTickets, setAllTickets] = useState<TicketSummaryDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<FilterTab>('open')

  const loadTickets = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getMyTickets()
      setAllTickets(data)
    } catch (err: any) {
      console.error('Failed to load tickets:', err)
      setError(err.message || 'Failed to load tickets')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTickets()
  }, [])

  // Filter tickets based on active tab
  const tickets = activeTab === 'open'
    ? allTickets.filter(
        (ticket) =>
          ticket.status !== TicketStatus.Resolved && ticket.status !== TicketStatus.Closed
      )
    : allTickets

  const handleTabChange = (event: React.SyntheticEvent, newValue: FilterTab) => {
    setActiveTab(newValue)
  }

  const handleCreateTicket = () => {
    navigate('/support/new')
  }

  const handleTicketClick = (ticketId: string) => {
    navigate(`/support/tickets/${ticketId}`)
  }

  const getEmptyMessage = () => {
    if (activeTab === 'open') {
      return 'You have no open tickets'
    }
    return 'You have not created any tickets yet'
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">My Support Tickets</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadTickets} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={handleCreateTicket}>
            New Ticket
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Open Tickets" value="open" />
          <Tab label="All Tickets" value="all" />
        </Tabs>
      </Box>

      {/* Ticket List */}
      <TicketList
        tickets={tickets}
        loading={loading}
        error={error}
        emptyMessage={getEmptyMessage()}
        onTicketClick={handleTicketClick}
      />
    </Container>
  )
}
